import React from "react";
import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
  Svg,
  Path,
  Circle,
} from "@react-pdf/renderer";
import type { IEstimatePayload, IEventExtras } from "./types";
import { HtmlContent } from "./HtmlContent";
import { BulletList } from "./BulletList";
import { withS3Prefix } from "../../../../../../helpers/withS3Prefix";
import { parseLabelGroups, ILabelGroup, parsePaymentPoliciesForPdf } from "../../../../../companyOutlet/helpers/labelGroupHelpers";

const NotoSansFont = require("../../../../../../ejs/fonts/NotoSans-Regular.ttf");
const NotoSansBold = require("../../../../../../ejs/fonts/NotoSans-Bold.ttf");

Font.register({
  family: "NotoSans",
  fonts: [
    { src: NotoSansFont, fontWeight: 400, fontStyle: "normal" },
    { src: NotoSansBold, fontWeight: 700, fontStyle: "normal" },
    { src: NotoSansFont, fontWeight: 400, fontStyle: "italic" },
    { src: NotoSansBold, fontWeight: 700, fontStyle: "italic" },
  ],
});

// Zoya Palace Colors
const BLACK = "#000000";
const GOLD = "#F4D26C";
const WHITE = "#ffffff";
const DARK_BAR = "#1a1a1a";
const GRAY_TEXT = "#999999";
const LIGHT_BORDER = "#111111";

// Banquet First platform logo (fallback to static asset)
const BANQUET_FIRST_LOGO = `${process.env.PUBLIC_URL || ""}/signLogo.png`;

const COVER_DATE_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Cover page date reads "DD/MM/YYYY" from the API — render it as "D MMM, YYYY" instead
const formatCoverDate = (dateStr?: string | null): string => {
  if (!dateStr) return "";
  const [dd, mm, yyyy] = dateStr.split("/");
  const day = parseInt(dd, 10);
  const monthIdx = parseInt(mm, 10) - 1;
  if (!dd || !mm || !yyyy || isNaN(day) || monthIdx < 0 || monthIdx > 11) return dateStr;
  return `${day} ${COVER_DATE_MONTHS[monthIdx]}, ${yyyy}`;
};

const s = StyleSheet.create({
  // Page 1: Cover
  coverPage: { backgroundColor: BLACK, padding: 0, position: "relative" },
  coverNote: { textAlign: "center", fontSize: 7, color: GRAY_TEXT, paddingTop: 12 },
  coverLogoWrap: { alignItems: "center", marginTop: 120 },
  coverLogo: { width: 180, height: 180, objectFit: "contain" },
  coverCongrats: { textAlign: "center", fontSize: 12, color: GOLD, letterSpacing: 6, marginTop: 80, fontWeight: "bold" },
  coverNames: { textAlign: "center", fontSize: 26, color: GOLD, letterSpacing: 5, marginTop: 16, fontWeight: "bold" },
  coverDateRow: { flexDirection: "row", alignItems: "center", justifyContent: "center", marginTop: 14 },
  coverDateLine: { width: 60, height: 1, backgroundColor: GOLD },
  coverDateText: { fontSize: 10, color: GOLD, marginHorizontal: 12 },
  coverFooter: { position: "absolute", bottom: 40, left: 0, right: 0, flexDirection: "row", justifyContent: "center", alignItems: "center", gap: 20 },
  coverFooterLeft: { textAlign: "left" },
  coverFooterRight: { borderLeftWidth: 1, borderLeftColor: GOLD, paddingLeft: 16, justifyContent: "center" },
  coverFooterText: { fontSize: 8, color: GOLD, lineHeight: 1.6 },
  coverFooterGold: { fontSize: 8, color: GOLD, lineHeight: 1.6 },
  coverFooterRow: { flexDirection: "row", alignItems: "center", gap: 5 },

  // Page 2: About & Proposal (black bg)
  darkPage: { backgroundColor: BLACK, paddingHorizontal: 36, paddingTop: 90, paddingBottom: 90, fontFamily: "NotoSans" },
  darkHeader: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 },
  darkCompanyName: { fontSize: 13, fontWeight: "bold", color: GOLD, marginBottom: 3 },
  darkAddress: { fontSize: 8, color: GRAY_TEXT, lineHeight: 1.5 },
  darkLogo: { width: 50, height: 50, objectFit: "contain" },
  darkDivider: { height: 1, backgroundColor: GOLD, marginVertical: 10 },
  darkSectionTitle: { fontSize: 11, fontWeight: "bold", color: GOLD, marginBottom: 8, marginTop: 12 },
  darkGalleryRow: { flexDirection: "row", gap: 10, marginTop: 20 },
  darkGalleryImg: { flex: 1, height: 160, objectFit: "cover", borderRadius: 8 },
  darkFooter: { position: "absolute", bottom: 0, left: 0, right: 0, backgroundColor: BLACK, paddingHorizontal: 36, paddingTop: 10, paddingBottom: 10, borderTopWidth: 0.5, borderTopColor: GOLD },

  // Fixed repeating header — solid black bar at top of pages 2,3,4
  fixedHeader: { position: "absolute", top: 0, left: 0, right: 0, backgroundColor: BLACK, paddingHorizontal: 36, paddingVertical: 14, flexDirection: "row", justifyContent: "space-between", alignItems: "center" },

  // Page 3: Invoice (white bg content)
  whitePage: { backgroundColor: WHITE, paddingHorizontal: 36, paddingTop: 110, paddingBottom: 70, fontFamily: "NotoSans", fontSize: 9 },
  whiteHeader: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", backgroundColor: DARK_BAR, padding: 14, marginBottom: 20 },
  whiteHeaderName: { fontSize: 12, fontWeight: "bold", color: GOLD },
  whiteHeaderAddr: { fontSize: 7.5, color: GRAY_TEXT, lineHeight: 1.5, marginTop: 2 },
  whiteHeaderLogo: { width: 55, height: 55, objectFit: "contain", borderWidth: 1, borderColor: GOLD },
  custGrid: { borderWidth: 1, borderColor: "#cccccc", borderRadius: 6, paddingVertical: 14, paddingHorizontal: 16, marginBottom: 20, backgroundColor: WHITE },
  custRow: { flexDirection: "row", marginBottom: 8 },
  custCell: { flex: 1, flexDirection: "row", alignItems: "center", gap: 6 },
  custLabel: { fontSize: 8.5, color: "#111111", fontWeight: "normal", fontFamily: "NotoSans" },
  custValue: { fontSize: 8.5, color: "#111111", fontWeight: "normal", fontFamily: "NotoSans" },
  eventTitle: { fontSize: 11, fontWeight: "bold", color: "#111111", marginBottom: 10, marginTop: 8 },
  goldTableHeader: { flexDirection: "row", backgroundColor: BLACK, paddingVertical: 9, paddingHorizontal: 8, marginBottom: 0 },
  goldThText: { fontSize: 8, fontWeight: "bold", color: GOLD },
  goldThTextWhite: { fontSize: 8, fontWeight: "bold", color: WHITE },
  invoiceRow: { flexDirection: "row", paddingVertical: 8, paddingHorizontal: 8, borderBottomWidth: 0.5, borderBottomColor: "#eeeeee", backgroundColor: WHITE },
  invoiceTd: { fontSize: 8.5, color: "#111111" },
  invoiceTdBold: { fontSize: 8.5, fontWeight: "bold", color: "#111111" },
  // Stay details table
  stayTitle: { fontSize: 11, fontWeight: "bold", color: "#111111", marginBottom: 10, marginTop: 16 },
  stayTableHeader: { flexDirection: "row", backgroundColor: BLACK, paddingVertical: 9, paddingHorizontal: 8 },
  stayThText: { fontSize: 7.5, fontWeight: "bold", color: GOLD },
  stayRow: { flexDirection: "row", paddingVertical: 7, paddingHorizontal: 8, borderBottomWidth: 0.5, borderBottomColor: "#eeeeee", backgroundColor: WHITE },
  stayTd: { fontSize: 7.5, color: "#111111" },
  stayTdBold: { fontSize: 7.5, fontWeight: "bold", color: "#111111" },
  finBox: { borderWidth: 1, borderColor: "#cccccc", borderRadius: 4, marginTop: 20, backgroundColor: WHITE },
  finRow: { flexDirection: "row", justifyContent: "space-between", paddingHorizontal: 14, paddingVertical: 9, borderBottomWidth: 0.5, borderBottomColor: "#eeeeee" },
  finLabel: { fontSize: 9, color: "#111111", fontWeight: "normal", fontFamily: "NotoSans" },
  finValue: { fontSize: 9, color: "#111111", textAlign: "right", fontFamily: "NotoSans" },
  remainBar: { flexDirection: "row", justifyContent: "space-between", backgroundColor: BLACK, borderBottomLeftRadius: 3, borderBottomRightRadius: 3, paddingVertical: 10, paddingHorizontal: 14 },
  remainLabel: { fontSize: 9.5, fontWeight: "bold", color: GOLD, fontFamily: "NotoSans" },
  remainAmount: { fontSize: 9.5, fontWeight: "bold", color: GOLD, fontFamily: "NotoSans" },
  bankBox: { borderWidth: 0.5, borderColor: "#dddddd", borderRadius: 4, marginTop: 14, padding: 14, flexDirection: "row", justifyContent: "space-between", backgroundColor: WHITE },
  bankTitle: { fontSize: 10, fontWeight: "bold", marginBottom: 8, color: "#111111" },
  bankText: { fontSize: 8, color: "#444444", lineHeight: 1.8 },
  bankQr: { width: 90, height: 90, objectFit: "contain" },
  paymentBox: { borderWidth: 0.5, borderColor: "#dddddd", borderRadius: 4, marginTop: 8, padding: 12, backgroundColor: WHITE },
  footerBar: { position: "absolute", bottom: 0, left: 0, right: 0, backgroundColor: DARK_BAR, paddingVertical: 10, alignItems: "center", borderTopWidth: 1, borderTopColor: GOLD },
  footerBarText: { fontSize: 9, fontWeight: "bold", color: GOLD, letterSpacing: 3 },

  // Repeating footer for pages after first
  repeatingFooter: { position: "absolute", bottom: 0, left: 0, right: 0, backgroundColor: BLACK, paddingHorizontal: 36, paddingTop: 10, paddingBottom: 10, borderTopWidth: 0.5, borderTopColor: GOLD },
  repeatingFooterRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 6 },
  repeatingFooterLeft: { flexDirection: "row", alignItems: "center", gap: 8 },
  repeatingFooterRight: { flexDirection: "column", alignItems: "center", gap: 1 },
  repeatingFooterLogo: { width: 30, height: 30, objectFit: "contain" },
  repeatingFooterBfLogo: { width: 60, height: 28, objectFit: "contain" },
  repeatingFooterName: { fontSize: 9, fontWeight: "bold", color: GOLD },
  repeatingFooterAddr: { fontSize: 7, color: GRAY_TEXT },
  repeatingFooterBfName: { fontSize: 13, fontWeight: "bold", color: WHITE },
  repeatingFooterNote: { fontSize: 6.5, color: GRAY_TEXT, textAlign: "center", marginTop: 2 },

  goldBar: { backgroundColor: GOLD, paddingVertical: 8, paddingHorizontal: 14, marginBottom: 16 },
  goldBarText: { fontSize: 10, fontWeight: "bold", color: BLACK, letterSpacing: 3, textAlign: "center" },
  sectionBorder: { border: "1px solid #111111", padding: "10px" },
  sectionTitle: { fontSize: 11, fontWeight: "bold", color: "#111111", marginBottom: 10, marginTop: 16, fontFamily: "NotoSans" },
  cardBox: { borderWidth: 0.5, borderColor: "#cccccc", borderRadius: 4, padding: 14, marginBottom: 10, backgroundColor: WHITE },
  cancelTitle: { fontSize: 11, fontWeight: "bold", color: "#111111", marginBottom: 10, marginTop: 20, fontFamily: "NotoSans" },
  cancelItemRow: { flexDirection: "row", alignItems: "flex-start", marginBottom: 6, paddingRight: 4 },
  cancelIcon: { fontSize: 7, color: "#111111", marginRight: 8, marginTop: 2, fontFamily: "NotoSans" },
  cancelText: { fontSize: 9, color: "#111111", flex: 1, lineHeight: 1.6, fontFamily: "NotoSans" },
  finalFooter: { position: "absolute", bottom: 20, left: 36, right: 36, flexDirection: "row", alignItems: "center", paddingTop: 14, borderTopWidth: 0.5, borderTopColor: GOLD },
  finalFooterLogo: { width: 50, height: 50, objectFit: "contain", marginRight: 12, borderWidth: 1, borderColor: GOLD },
  finalFooterName: { fontSize: 10, fontWeight: "bold", color: GOLD },
  finalFooterAddr: { fontSize: 8, color: GRAY_TEXT, marginTop: 2 },
  finalFooterNote: { fontSize: 7, color: GRAY_TEXT, marginLeft: "auto", textAlign: "right", maxWidth: 180 },
});

interface IOutletQuotationDocumentProps {
  data: IEstimatePayload[];
  imageCache?: Record<string, string>;
  appLogo?: string;
  appName?: string;
}

export const OutletQuotationDocument: React.FC<IOutletQuotationDocumentProps> = ({ data, imageCache = {}, appLogo, appName }) => {
  if (!data || data.length === 0) return null;

  const company = data[0];
  let paymentQr: any[] = [];
  try { paymentQr = JSON.parse(company.payment_qr || "[]"); } catch { }
  let gallery: string[] = [];
  if (Array.isArray(company.gallery)) {
    gallery = company.gallery;
  } else {
    try { gallery = JSON.parse(company.gallery || "[]"); } catch { }
  }

  // Package Highlights — lead-level attachments (distinct from the outlet `gallery`)
  const rawAttachments: any[] = Array.isArray(company.attachments)
    ? company.attachments
    : (() => { try { return JSON.parse((company.attachments as any) || "[]"); } catch { return []; } })();
  const packageHighlights: string[] = rawAttachments
    .map((att) => (typeof att === "string" ? att : att?.path))
    .filter(Boolean);


  let eventExtras: IEventExtras = {};
  if (company.extras && typeof company.extras === "object" && !Array.isArray(company.extras)) {
    eventExtras = company.extras;
  } else if (typeof company.extras === "string") {
    try { eventExtras = JSON.parse(company.extras || "{}"); } catch { }
  }
  const customCoverTitle = eventExtras.event_title?.trim();
  const coverHeading = customCoverTitle
    ? customCoverTitle.toUpperCase()
    : (company.event_type?.toUpperCase() || "");

  const address = `${company.billing_address_1}\n${company.billing_address_2}\n${company.billing_city}, ${company.billing_state}\n ${company.billing_country}, ${company.billing_zip_code}`;
  const rawLogoUrl = company.logo ? withS3Prefix(company.logo) : "";
  const logoUrl = imageCache[rawLogoUrl] || (rawLogoUrl ? rawLogoUrl + "?x-cache-bust=pdf" : "");

  // Helper to resolve image URL through cache, with cache-bust for CORS
  const resolveImg = (url: string) => {
    if (imageCache[url]) return imageCache[url];
    return url ? url + "?x-cache-bust=pdf" : "";
  };

  // App platform logo (from useAppConfig)
  const appLogoUrl = appLogo ? (appLogo + "?x-cache-bust=pdf") : BANQUET_FIRST_LOGO;
  const appDisplayName = "Banquet First";

  return (
    <Document>

      {/* PAGE 1: COVER */}
      <Page size="A4" style={s.coverPage}>
        <Text style={s.coverNote}>Note: This quotation is provided for estimation purposes only and is not a valid tax invoice</Text>
        <View style={s.coverLogoWrap}>
          {logoUrl ? <Image src={logoUrl} style={s.coverLogo} /> : null}
        </View>
        <Text style={s.coverCongrats}>C O N G R A T U L A T I O N</Text>
        <Text style={s.coverNames}>{coverHeading}</Text>
        <View style={s.coverDateRow}>
          <View style={s.coverDateLine} />
          <Text style={s.coverDateText}>{formatCoverDate(company.function_date)}</Text>
          <View style={s.coverDateLine} />
        </View>
        <View style={s.coverFooter}>
          <View style={s.coverFooterLeft}>
            <Text style={s.coverFooterText}>{company.billing_address_1}, {company.billing_address_2}</Text>
            <Text style={s.coverFooterText}>{company.billing_city}, {company.billing_state}, {company.billing_zip_code}</Text>
          </View>
          <View style={s.coverFooterRight}>
            {(company.company_outlet_phone || company.phone) && (
              <View style={s.coverFooterRow}>
                <Svg width="9" height="9" viewBox="0 0 24 24">
                  <Path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.6.1.3 0 .7-.2 1l-2.3 2.2z" fill={GOLD} />
                </Svg>
                <Text style={s.coverFooterGold}>
                  {company.country_code ? `${company.country_code} ` : ""}{company.company_outlet_phone || company.phone}
                </Text>
              </View>
            )}
            {(company.company_outlet_email || company.whatsapp_number) && (
              <View style={s.coverFooterRow}>
                <Svg width="9" height="9" viewBox="0 0 24 24">
                  <Path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm6.93 6h-2.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.9 4.33 3.56zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2s.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.89-4.33-3.56zm2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2s.07-1.35.16-2h4.68c.09.65.16 1.32.16 2s-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95c-.96 1.65-2.49 2.93-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2s-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z" fill={GOLD} />
                </Svg>
                <Text style={s.coverFooterGold}>{company.company_outlet_email || company.whatsapp_number}</Text>
              </View>
            )}
          </View>
        </View>
      </Page>

      {/* PAGE 2: ABOUT & PROPOSAL (black bg) */}
      <Page size="A4" style={s.darkPage}>
        {/* Fixed header - repeats on this page */}
        <View style={s.fixedHeader} fixed>
          <View>
            <Text style={s.darkCompanyName}>{company.company_name}</Text>
            <Text style={s.darkAddress}>{address}</Text>
          </View>
          {logoUrl ? <Image src={logoUrl} style={s.darkLogo} /> : null}
        </View>
        <View style={s.darkDivider} />

        {company.banquet_description && (
          <>
            <Text style={s.darkSectionTitle} minPresenceAhead={40}>ABOUT OUR BANQUET</Text>
            <BulletList html={company.banquet_description} />
          </>
        )}

        {company.proposal && (
          <>
            <Text style={s.darkSectionTitle} minPresenceAhead={40}>OUR PROPOSAL</Text>
            <BulletList html={company.proposal} />
          </>
        )}

        {gallery.length > 0 && (
          <View style={s.darkGalleryRow}>
            {gallery.slice(0, 3).map((img, i) => (
              <Image src={resolveImg(typeof img === "string" ? withS3Prefix(img) : "")} style={s.darkGalleryImg} key={`gal-${i}`} />
            ))}
          </View>
        )}

        {packageHighlights.length > 0 && (
          <View>
            <Text style={s.darkSectionTitle} minPresenceAhead={60}>PACKAGE HIGHLIGHTS</Text>
            {[0, 3].map((start) => {
              const row = packageHighlights.slice(start, start + 3);
              if (row.length === 0) return null;
              return (
                <View style={[s.darkGalleryRow, start > 0 ? { marginTop: 10 } : {}]} key={`ph-row-${start}`} wrap={false}>
                  {row.map((img, i) => (
                    <Image src={resolveImg(withS3Prefix(img))} style={s.darkGalleryImg} key={`ph-${start + i}`} />
                  ))}
                </View>
              );
            })}
          </View>
        )}

        <View style={s.darkFooter} fixed>
          <View style={s.repeatingFooterRow}>
            <View style={s.repeatingFooterLeft}>
              {logoUrl ? <Image src={logoUrl} style={s.repeatingFooterLogo} /> : null}
              <View>
                <Text style={s.repeatingFooterName}>{company.company_name}</Text>
                <Text style={s.repeatingFooterAddr}>{company.billing_address_1}, {company.billing_city}, {company.billing_state}</Text>
              </View>
            </View>
            <View style={s.repeatingFooterRight}>
              <Image src={appLogoUrl} style={s.repeatingFooterBfLogo} />
              <Text style={s.repeatingFooterBfName}>{appDisplayName}</Text>
            </View>
          </View>
          <Text style={s.repeatingFooterNote}>Note: This quotation is provided for estimation purposes only and is not a valid tax invoice</Text>
        </View>
      </Page>


      {/* PAGE 3: INVOICE (dark bg) */}
      <Page size="A4" style={s.whitePage}>
        {/* Fixed header - repeats on every page */}
        <View style={s.fixedHeader} fixed>
          <View>
            <Text style={s.darkCompanyName}>{company.company_name}</Text>
            <Text style={s.darkAddress}>{address}</Text>
          </View>
          {logoUrl ? <Image src={logoUrl} style={s.darkLogo} /> : null}
        </View>

        {/* Customer info grid */}
        <View style={s.custGrid}>
          <View style={s.custRow}>
            {/* Name */}
            <View style={s.custCell}>
              <Svg width="11" height="11" viewBox="0 0 24 24"><Path d="M12 12c2.7 0 5-2.3 5-5s-2.3-5-5-5-5 2.3-5 5 2.3 5 5 5zm0 2c-3.3 0-10 1.7-10 5v3h20v-3c0-3.3-6.7-5-10-5z" fill="#111111" /></Svg>
              <Text style={s.custLabel}>{company.name_of_guest}</Text>
            </View>
            {/* Date */}
            <View style={s.custCell}>
              <Svg width="11" height="11" viewBox="0 0 24 24"><Path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z" fill="#111111" /></Svg>
              <Text style={s.custLabel}>{company.function_date}</Text>
            </View>
            {/* Time */}
            <View style={s.custCell}>
              <Svg width="11" height="11" viewBox="0 0 24 24"><Path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8zm.5-13H11v6l5.2 3.2.8-1.3-4.5-2.7V7z" fill="#111111" /></Svg>
              <Text style={s.custLabel}>{company.event_time}</Text>
            </View>
            {/* Estimate No */}
            <View style={s.custCell}>
              <Svg width="11" height="11" viewBox="0 0 24 24"><Circle cx="12" cy="12" r="10" fill="#111111" /></Svg>
              <Text style={s.custLabel}>{company.estimate_number}</Text>
            </View>
          </View>
          <View style={s.custRow}>
            {/* Phone */}
            <View style={s.custCell}>
              <Svg width="11" height="11" viewBox="0 0 24 24"><Path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.6.1.3 0 .7-.2 1l-2.3 2.2z" fill="#111111" /></Svg>
              <Text style={s.custLabel}>{company.country_code} {company.contact_number_one}</Text>
            </View>
            {/* Event type */}
            <View style={s.custCell}>
              <Svg width="11" height="11" viewBox="0 0 24 24"><Path d="M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1z" fill="#111111" /></Svg>
              <Text style={s.custLabel}>{company.event_type}</Text>
            </View>
            {/* Guests */}
            <View style={s.custCell}>
              <Svg width="11" height="11" viewBox="0 0 24 24"><Path d="M16 11c1.7 0 3-1.3 3-3s-1.3-3-3-3-3 1.3-3 3 1.3 3 3 3zm-8 0c1.7 0 3-1.3 3-3S9.7 5 8 5 5 6.3 5 8s1.3 3 3 3zm0 2c-2.3 0-7 1.2-7 3.5V19h14v-2.5c0-2.3-4.7-3.5-7-3.5zm8 0c-.3 0-.6 0-.9 0 1.1.8 1.9 1.9 1.9 3.5V19h6v-2.5c0-2.3-4.7-3.5-7-3.5z" fill="#111111" /></Svg>
              <Text style={s.custLabel}>{company.number_of_guests}+</Text>
            </View>
            {/* Venue */}
            <View style={s.custCell}>
              <Svg width="11" height="11" viewBox="0 0 24 24"><Path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5c-1.4 0-2.5-1.1-2.5-2.5S10.6 6.5 12 6.5s2.5 1.1 2.5 2.5S13.4 11.5 12 11.5z" fill="#111111" /></Svg>
              <Text style={s.custLabel}>{company.preferred_venue}</Text>
            </View>
          </View>
        </View>

        {/* Per-event items — stay_details rendered separately after all events */}
        {data.map((event, idx) => (
          <View key={idx}>
            {/* items_charges table */}
            {event.items_charges && event.items_charges.length > 0 && (
              <View wrap={false}>
                <Text style={s.eventTitle}>
                  {event.event_type?.toUpperCase() === "OTHER SOCIAL" && event.other_event_name
                    ? event.other_event_name.toUpperCase()
                    : event.event_type?.toUpperCase()}
                </Text>
                <View style={s.goldTableHeader}>
                  <Text style={[s.goldThText, { width: "22%" }]}>Item Name</Text>
                  <Text style={[s.goldThText, { width: "6%" }]}>Qty</Text>
                  <Text style={[s.goldThText, { width: "13%" }]}>Unit Price</Text>
                  <Text style={[s.goldThText, { width: "8%" }]}>Disc%</Text>
                  <Text style={[s.goldThText, { width: "13%" }]}>Disc Amount</Text>
                  <Text style={[s.goldThText, { width: "7%" }]}>Tax%</Text>
                  <Text style={[s.goldThText, { width: "13%" }]}>Tax Amount</Text>
                  <Text style={[s.goldThText, { width: "18%", textAlign: "right" }]}>Total</Text>
                </View>
                {event.items_charges.map((item, i) => (
                  <View style={s.invoiceRow} key={i}>
                    <Text style={[s.invoiceTd, { width: "22%" }]}>{item.name}</Text>
                    <Text style={[s.invoiceTd, { width: "6%" }]}>{item.count}</Text>
                    <Text style={[s.invoiceTd, { width: "13%" }]}>{item.amount}</Text>
                    <Text style={[s.invoiceTd, { width: "8%" }]}>{item.discount}{item.discount_mode === "percentage" ? "%" : ""}</Text>
                    <Text style={[s.invoiceTd, { width: "13%" }]}>{item.discount_amount || "0"}</Text>
                    <Text style={[s.invoiceTd, { width: "7%" }]}>
                      {item.tax_type === "VAT"
                        ? `${item.vat_percentage || 0}% VAT`
                        : `${item.gst_percentage}% GST`}
                    </Text>
                    <Text style={[s.invoiceTd, { width: "13%" }]}>
                      {item.tax_type === "VAT" ? (item.vat_amount || "0") : item.gst_amount}
                    </Text>
                    <Text style={[s.invoiceTdBold, { width: "18%", textAlign: "right" }]}>{item.total}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        ))}

        {/* Stay Details — rendered once after all event tables */}
        {(() => {
          const allStay = data.flatMap(e => e.stay_details || []).filter(Boolean);
          if (allStay.length === 0) return null;
          return (
            <View wrap={false}>
              <Text style={s.stayTitle}>STAY DETAILS</Text>
              <View style={s.stayTableHeader}>
                <Text style={[s.stayThText, { width: "18%" }]}>Room</Text>
                <Text style={[s.stayThText, { width: "12%" }]}>Check In</Text>
                <Text style={[s.stayThText, { width: "12%" }]}>Check Out</Text>
                <Text style={[s.stayThText, { width: "7%" }]}>Rooms</Text>
                <Text style={[s.stayThText, { width: "7%" }]}>Nights</Text>
                <Text style={[s.stayThText, { width: "11%" }]}>Price</Text>
                <Text style={[s.stayThText, { width: "8%" }]}>Disc%</Text>
                <Text style={[s.stayThText, { width: "7%" }]}>GST%</Text>
                <Text style={[s.stayThText, { width: "10%" }]}>GST Amt</Text>
                <Text style={[s.stayThText, { width: "8%", textAlign: "right" }]}>Total</Text>
              </View>
              {allStay.map((stay, i) => (
                <View style={s.stayRow} key={i}>
                  <Text style={[s.stayTd, { width: "18%" }]}>{stay.room_name}</Text>
                  <Text style={[s.stayTd, { width: "12%" }]}>{stay.check_in || "-"}</Text>
                  <Text style={[s.stayTd, { width: "12%" }]}>{stay.check_out || "-"}</Text>
                  <Text style={[s.stayTd, { width: "7%" }]}>{stay.rooms}</Text>
                  <Text style={[s.stayTd, { width: "7%" }]}>{stay.nights}</Text>
                  <Text style={[s.stayTd, { width: "11%" }]}>{stay.price}</Text>
                  <Text style={[s.stayTd, { width: "8%" }]}>{stay.discount}{stay.discount_mode === "percentage" ? "%" : ""}</Text>
                  <Text style={[s.stayTd, { width: "7%" }]}>{stay.gst_percentage}%</Text>
                  <Text style={[s.stayTd, { width: "10%" }]}>{stay.gst_amount}</Text>
                  <Text style={[s.stayTdBold, { width: "8%", textAlign: "right" }]}>{stay.total}</Text>
                </View>
              ))}
            </View>
          );
        })()}

        {/* Financial Summary — totals combined across all events */}
        {(() => {
          const parseNum = (v: string | number | null | undefined): number => {
            if (v === null || v === undefined || v === "") return 0;
            const n = parseFloat(String(v).replace(/[₹,\s]/g, ""));
            return isNaN(n) ? 0 : n;
          };
          const fmt = (n: number) => n.toLocaleString("en-IN");

          // Calculate from top-level fields first; fallback to sum from items if 0
          let subTotal = data.reduce((s, e) => s + parseNum(e.sub_total), 0);
          let totalGst = data.reduce((s, e) => s + parseNum(e.total_gst), 0);
          let totalVat = data.reduce((s, e) => s + parseNum(e.total_vat), 0);
          let totalDiscount = data.reduce((s, e) => s + parseNum(e.total_discount), 0);
          let grandTotal = data.reduce((s, e) => s + parseNum(e.grand_total), 0);

          // Fallback: compute from items_charges + stay_details if top-level is 0
          if (grandTotal === 0) {
            data.forEach((e) => {
              (e.items_charges || []).forEach((item) => {
                subTotal += parseNum(item.amount) * parseNum(item.count);
                totalGst += parseNum(item.gst_amount);
                totalVat += parseNum(item.vat_amount);
                totalDiscount += parseNum(item.discount_amount);
                grandTotal += parseNum(item.total);
              });
              (e.stay_details || []).forEach((stay) => {
                grandTotal += parseNum(stay.total);
              });
            });
          }

          const effectiveTotal = data.reduce((s, e) => s + parseNum(e.sub_total_less_discount), 0) || (subTotal - totalDiscount);

          // amount_paid and remaining_balance come from the last record (they are lead-level, not per-event)
          const last = data[data.length - 1];
          const amountPaid = parseNum(last.total_amount_paid);
          const remainingBalance = parseNum(last.remaining_balance) || (grandTotal - amountPaid);

          return (
            <View wrap={false} style={{ borderWidth: 1, borderColor: "#cccccc", borderRadius: 4, marginTop: 20 }}>
              <View style={s.finRow}><Text style={s.finLabel}>Sub Total</Text><Text style={s.finValue}>{fmt(subTotal)}</Text></View>
              <View style={s.finRow}><Text style={s.finLabel}>Effective total</Text><Text style={s.finValue}>{fmt(effectiveTotal)}</Text></View>
              <View style={s.finRow}><Text style={s.finLabel}>Total GST</Text><Text style={s.finValue}>{fmt(totalGst)}</Text></View>
              {totalVat > 0 && (
                <View style={s.finRow}><Text style={s.finLabel}>Total VAT</Text><Text style={s.finValue}>{fmt(totalVat)}</Text></View>
              )}
              <View style={s.finRow}><Text style={s.finLabel}>Total Discount</Text><Text style={s.finValue}>{fmt(totalDiscount)}</Text></View>
              <View style={s.finRow}><Text style={s.finLabel}>Grand Total</Text><Text style={s.finValue}>{fmt(grandTotal)}</Text></View>
              <View style={{ ...s.finRow, borderBottomWidth: 0 }}><Text style={s.finLabel}>Amount Paid</Text><Text style={s.finValue}>{fmt(amountPaid)}</Text></View>
              <View style={s.remainBar}>
                <Text style={s.remainLabel}>Remaining Balance</Text>
                <Text style={s.remainAmount}>{fmt(remainingBalance)}</Text>
              </View>
            </View>
          );
        })()}

        {/* Bank Details card — kept on the same page as the invoice/financials to avoid a
            near-empty trailing page (heading + plain bank lines + bulleted payment terms,
            all parsed from payment_mode_policies) */}
        {company.payment_mode_policies && company.payment_mode_policies.trim() !== "" && (() => {
          const parsedPolicies = parsePaymentPoliciesForPdf(company.payment_mode_policies);
          const hasBankLines = parsedPolicies.bankLines.length > 0;
          return (
            <View wrap={false} style={{ borderWidth: 1, borderColor: "#cccccc", borderRadius: 6, padding: 16, marginTop: 20 }}>
              <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 11, fontWeight: "bold", color: "#111111", marginBottom: 10, fontFamily: "NotoSans" }}>
                    {parsedPolicies.heading || "BANK DETAILS"}
                  </Text>
                  {hasBankLines ? (
                    parsedPolicies.bankLines.map((line, i) => (
                      <Text key={`bank-${i}`} style={{ fontSize: 9, color: "#333333", lineHeight: 1.8, fontFamily: "NotoSans" }}>{line}</Text>
                    ))
                  ) : (
                    <Text style={{ fontSize: 9, color: "#333333", lineHeight: 1.8, fontFamily: "NotoSans" }}>Contact for bank details</Text>
                  )}
                </View>
                {paymentQr.length > 0 && (
                  <Image src={resolveImg(withS3Prefix(typeof paymentQr[0] === "string" ? paymentQr[0] : (paymentQr[0]?.path || "")))} style={{ width: 100, height: 100, objectFit: "contain" }} />
                )}
              </View>
              {parsedPolicies.paymentTerms.length > 0 && (
                <View style={{ marginTop: 10, paddingTop: 10, borderTopWidth: 0.5, borderTopColor: "#e0e0e0" }}>
                  {parsedPolicies.paymentTerms.map((line, i) => (
                    <View style={{ flexDirection: "row", alignItems: "flex-start", marginBottom: 6, paddingRight: 4 }} key={`pt-${i}`}>
                      <Svg width="7" height="8" viewBox="0 0 10 12" style={{ marginRight: 8, marginTop: 2, flexShrink: 0 } as any}>
                        <Path d="M0 0 L10 6 L0 12 Z" fill="#111111" />
                      </Svg>
                      <Text style={{ fontSize: 9, color: "#333333", flex: 1, lineHeight: 1.6, fontFamily: "NotoSans" }}>{line}</Text>
                    </View>
                  ))}
                </View>
              )}
            </View>
          );
        })()}

        {/* Inclusions / Exclusions / Company Policies / Cancellation Policies — kept flowing
            on this same page (no hard page break) so they move up onto any space left over
            after the invoice/financials/bank details instead of always starting a fresh page. */}

        {/* Inclusions — heading + box move as one atomic unit so the heading can
            never get orphaned on a page while the box it labels lands on the next
            one (minPresenceAhead alone wasn't enough once a group ran past ~60pt) */}
        {company.inclusion && (Array.isArray(company.inclusion) ? company?.inclusion?.length > 0 : company?.inclusion?.trim() !== "") && (
          <View wrap={false}>
            <Text style={s.sectionTitle}>INCLUSIONS</Text>
            <View style={s.sectionBorder}>
              <LabelGroupGrid groups={parseLabelGroups(company.inclusion)} icon="check" />
            </View>
          </View>
        )}

        {/* Exclusions — same atomic heading+box treatment as Inclusions */}
        {company.exclusion && (Array.isArray(company.exclusion) ? company?.exclusion?.length > 0 : company?.exclusion?.trim() !== "") && (
          <View wrap={false}>
            <Text style={s.sectionTitle}>EXCLUSIONS</Text>
            <View style={s.sectionBorder}>
              <LabelGroupGrid groups={parseLabelGroups(company.exclusion)} icon="check" layout="full" />
            </View>
          </View>
        )}

        {/* Company Policies & Terms */}
        {company.policies && company.policies.trim() !== "" && (
          <View>
            <Text style={s.cancelTitle} minPresenceAhead={40}>COMPANY POLICIES & TERMS</Text>
            {htmlToLines(company.policies).map((line, i) => (
              <View style={s.cancelItemRow} key={`pol-${i}`} wrap={false}>
                <Svg width="7" height="8" viewBox="0 0 10 12" style={{ marginRight: 8, marginTop: 2 } as any}>
                  <Path d="M0 0 L10 6 L0 12 Z" fill="#111111" />
                </Svg>
                <Text style={s.cancelText}>{line}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Cancellation Policies */}
        {company.cancellation_policies && company.cancellation_policies.trim() !== "" && (
          <View>
            <Text style={s.cancelTitle} minPresenceAhead={40}>CANCELLATION POLICIES</Text>
            {htmlToLines(company.cancellation_policies).map((line, i) => (
              <View style={s.cancelItemRow} key={`cp-${i}`} wrap={false}>
                <Svg width="7" height="8" viewBox="0 0 10 12" style={{ marginRight: 8, marginTop: 2 } as any}>
                  <Path d="M0 0 L10 6 L0 12 Z" fill="#111111" />
                </Svg>
                <Text style={s.cancelText}>{line}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Repeating footer - outlet on left, Banquet First on right, note below */}
        <View style={s.repeatingFooter} fixed>
          <View style={s.repeatingFooterRow}>
            <View style={s.repeatingFooterLeft}>
              {logoUrl ? <Image src={logoUrl} style={s.repeatingFooterLogo} /> : null}
              <View>
                <Text style={s.repeatingFooterName}>{company.company_name}</Text>
                <Text style={s.repeatingFooterAddr}>{company.billing_address_1}, {company.billing_city}, {company.billing_state}</Text>
              </View>
            </View>
            <View style={s.repeatingFooterRight}>
              <Image src={appLogoUrl} style={s.repeatingFooterBfLogo} />
              <Text style={s.repeatingFooterBfName}>{appDisplayName}</Text>
            </View>
          </View>
          <Text style={s.repeatingFooterNote}>Note: This quotation is provided for estimation purposes only and is not a valid tax invoice</Text>
        </View>
      </Page>

    </Document>
  );
};

// ─── Label Group Grid (Inclusions / Exclusions structured rendering) ─────────

// Helper: strip HTML tags and split into lines
function htmlToLines(html: string): string[] {
  if (!html) return [];
  let text = html.replace(/<br\s*\/?>/gi, "\n");
  text = text.replace(/<\/p>/gi, "\n");
  text = text.replace(/<\/li>/gi, "\n");
  text = text.replace(/<[^>]*>/g, "");
  text = text.replace(/&amp;/g, "&");
  text = text.replace(/&lt;/g, "<");
  text = text.replace(/&gt;/g, ">");
  text = text.replace(/&nbsp;/g, " ");
  text = text.replace(/&quot;/g, '"');
  return text.split("\n").map((l) => l.trim()).filter(Boolean);
}

const lgStyles = StyleSheet.create({
  container: { flexDirection: "row", flexWrap: "wrap" },
  card: {
    width: "48%",
    marginBottom: 10,
    marginRight: "2%",
    paddingHorizontal: 4,
  },
  groupLabel: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#1a1a1a",
    marginBottom: 6,
    fontFamily: "NotoSans",
  },
  itemRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 4,
    paddingRight: 4,
  },
  checkIcon: {
    fontSize: 8.5,
    color: "#111111",
    marginRight: 7,
    marginTop: 1,
    fontFamily: "NotoSans",
  },
  itemText: {
    fontSize: 9,
    color: "#111111",
    flex: 1,
    lineHeight: 1.5,
    fontFamily: "NotoSans",
  },
});

interface ILabelGroupGridProps {
  groups: ILabelGroup[];
  icon?: "check" | "cross";
  layout?: "grid" | "full";
}

const LabelGroupGrid: React.FC<ILabelGroupGridProps> = ({ groups, icon = "check", layout = "grid" }) => {
  const filteredGroups = groups.filter(
    (g) => g.label.trim() || g.items.some((i) => i.value.trim())
  );

  if (filteredGroups.length === 0) return null;

  const CheckIcon = () => (
    <Svg width="12" height="12" viewBox="0 0 24 24" style={{ marginRight: 8, marginTop: 0, flexShrink: 0 } as any}>
      <Path d="M8.5 16.5L3.5 11.5L1.5 13.5L8.5 20.5L22.5 6.5L20.5 4.5L8.5 16.5Z" fill="#111111" />
    </Svg>
  );

  const CrossIcon = () => (
    <Svg width="12" height="12" viewBox="0 0 24 24" style={{ marginRight: 8, marginTop: 0, flexShrink: 0 } as any}>
      <Path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="#111111" />
    </Svg>
  );

  const ItemIcon = icon === "cross" ? CrossIcon : CheckIcon;
  const isFullWidth = layout === "full";

  return (
    <View style={isFullWidth ? {} : lgStyles.container}>
      {filteredGroups.map((group) => (
        <View style={isFullWidth ? { width: "100%", marginBottom: 10, paddingHorizontal: 4 } : lgStyles.card} key={group.id} wrap={false}>
          {group.label.trim() && (
            <Text style={lgStyles.groupLabel}>{group.label}</Text>
          )}
          {group.items
            .filter((item) => item.value.trim())
            .map((item) => (
              <View style={lgStyles.itemRow} key={item.id}>
                <ItemIcon />
                <Text style={lgStyles.itemText}>{item.value}</Text>
              </View>
            ))}
        </View>
      ))}
    </View>
  );
};
